// Which edges of a sideways scroller have content hidden beyond them.
//
// One answer for "show a fade where there is more": ModuleTabBar (tabs beyond
// the strip), TableScroll (columns beyond the table) and the Activity feed's
// kind chips all need the same two booleans, and each used to compute them
// itself. The scroller is the element you attach `ref` to; the caller draws the
// fade (its colour must match what the scroller sits on).
//
// `dep` is anything that changes what the scroller holds (a tab or chip count):
// the fades are re-read when it changes, as well as on scroll and on resize.
import { useEffect, useState, type RefObject } from 'react'

export interface EdgeFades { left: boolean; right: boolean }

/** The rule: content is hidden left when scrolled in, right when not at the end (1px slack for sub-pixel rounding). */
export function edgeFadesOf(m: { scrollLeft: number; clientWidth: number; scrollWidth: number }): EdgeFades {
  return {
    left:  m.scrollLeft > 1,
    right: m.scrollLeft + m.clientWidth < m.scrollWidth - 1,
  }
}

export function useEdgeFades(strip: RefObject<HTMLElement>, dep: unknown = null): EdgeFades {
  const [fade, setFade] = useState<EdgeFades>({ left: false, right: false })
  useEffect(() => {
    const el = strip.current
    if (!el) return
    const update = () => {
      const next = edgeFadesOf(el)
      // Same answer = same state object, so a scroll inside the strip does not re-render.
      setFade(prev => (prev.left === next.left && prev.right === next.right ? prev : next))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    ro?.observe(el)
    return () => { el.removeEventListener('scroll', update); ro?.disconnect() }
  }, [strip, dep])
  return fade
}
